import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Permission } from '@re/shared';
import { getUiLanguage, tr } from '../lib/i18n';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { PermissionRow, Role } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/button';
import { ConfirmDialog, Empty, ErrorState } from '../components/common';
import { PageActions } from '../components/page-actions';
import { TextField } from '../components/text-field';
import { useToast } from '../components/Toast';
import { PERMISSION_GROUP, PERMISSION_LABEL_EN } from '../lib/labels';

const SYSTEM_ROLE_AR: Record<string, string> = {
  OWNER: 'مالك',
  MANAGER: 'مدير',
  EDITOR: 'محرر',
  DATA_ENTRY: 'مدخل بيانات',
  SALES: 'مبيعات',
};
const SYSTEM_ROLE_EN: Record<string, string> = {
  OWNER: 'Owner',
  MANAGER: 'Manager',
  EDITOR: 'Editor',
  DATA_ENTRY: 'Data entry',
  SALES: 'Sales',
};
const roleNameAr = (role: Role) => (role.is_system ? SYSTEM_ROLE_AR[role.code] : undefined) ?? role.name;
const roleNameEn = (role: Role) => (role.is_system ? SYSTEM_ROLE_EN[role.code] : undefined) ?? role.nameEn ?? role.name_en ?? '';
const roleLabel = (role: Role) => getUiLanguage() === 'en' ? roleNameEn(role) || roleNameAr(role) : roleNameAr(role);

function usePermissions() {
  return useQuery({ queryKey: ['permissions'], queryFn: () => api<{ items: PermissionRow[] }>('/permissions'), staleTime: Infinity });
}

function groupsOf(all: PermissionRow[], on: string[]) {
  const map = new Map<string, { key: string; total: number; on: number; items: PermissionRow[] }>();
  for (const p of all) {
    const g = map.get(p.group_key) ?? { key: p.group_key, total: 0, on: 0, items: [] };
    g.total++;
    g.items.push(p);
    if (on.includes(p.code)) g.on++;
    map.set(p.group_key, g);
  }
  return [...map.values()];
}

export function RolesPage() {
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<{ items: Role[] }>('/roles'), placeholderData: keepPreviousData });
  const perms = usePermissions();
  const { me } = useAuth();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [picked, setPicked] = useState<string | 'new' | null>(null);
  const [draftNames, setDraftNames] = useState({ ar: '', en: '' });
  const [saveState, setSaveState] = useState<{ run: () => void; pending: boolean; disabled: boolean } | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api(`/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(tr('تم حذف الدور', 'Role deleted'));
      setDeleting(null);
      setPicked(null);
      void qc.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: toastError,
  });

  const items = roles.data?.items ?? [];
  const selectedId = picked === 'new' || (picked && items.some((r) => r.id === picked)) ? picked : (items[0]?.id ?? null);
  const selected = selectedId && selectedId !== 'new' ? items.find((r) => r.id === selectedId) ?? null : null;
  const loading = (roles.isLoading && !roles.data) || (perms.isLoading && !perms.data);
  const permissionItems = perms.data?.items ?? [];

  return (
    <>
      <PageActions>
        <Button variant="contained" onClick={() => saveState?.run()} loading={!!saveState?.pending} disabled={!saveState || saveState.disabled}>{tr('حفظ', 'Save')}</Button>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setPicked('new'); setDraftNames({ ar: '', en: '' }); }}>{tr('دور مخصص', 'Custom role')}</Button>
      </PageActions>
      {roles.isError ? <ErrorState error={roles.error} onRetry={() => roles.refetch()} /> : perms.isError ? (
        <ErrorState error={perms.error} onRetry={() => perms.refetch()} />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            flex: 1,
            minHeight: 0,
          }}
        >
          <Card
            sx={{
              width: { md: 220 },
              flexShrink: 0,
              height: { md: '100%' },
              maxHeight: { xs: 240, md: 'none' },
              overflow: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {loading ? (
              <Stack sx={{ py: 1 }}>{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={52} sx={{ mx: 1.5 }} />)}</Stack>
            ) : (
              <List disablePadding>
                {items.map((r) => {
                  const mine = r.id === me?.user.role_id;
                  return (
                    <ListItemButton key={r.id} selected={r.id === selectedId} onClick={() => { setPicked(r.id); setDraftNames({ ar: roleNameAr(r), en: roleNameEn(r) }); }}>
                      <ListItemText
                        primary={r.id === selectedId && !r.is_system && (draftNames.ar || draftNames.en)
                          ? (getUiLanguage() === 'en' ? draftNames.en || draftNames.ar : draftNames.ar)
                          : roleLabel(r)}
                        secondary={`${r.users_count} ${tr('موظف', 'employees')}${mine ? tr(' · دورك', ' · Your role') : ''}`}
                      />
                      {r.code === 'OWNER' && <LockIcon fontSize="small" color="disabled" />}
                    </ListItemButton>
                  );
                })}
                {selectedId === 'new' && (
                  <ListItemButton selected>
                    <ListItemText primary={(getUiLanguage() === 'en' ? draftNames.en : draftNames.ar).trim() || tr('دور جديد', 'New role')} secondary={tr('غير محفوظ', 'Unsaved')} />
                  </ListItemButton>
                )}
              </List>
            )}
          </Card>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: { md: '100%' },
              overflow: { md: 'auto' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {loading ? (
              <Card>
                <Stack sx={{ p: 2.5 }} spacing={1.5}>
                  <Skeleton height={56} />
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <Skeleton key={i} height={36} />)}
                </Stack>
              </Card>
            ) : !permissionItems.length ? (
              <Card sx={{ p: 3 }}>
                <Empty text={tr('قائمة الصلاحيات فارغة — تأكد من تشغيل seed قاعدة البيانات (permissions).', 'The permissions list is empty — make sure the permissions database seed has run.')} />
              </Card>
            ) : selectedId && (selectedId === 'new' || selected) ? (
              <RoleEditor
                key={selectedId}
                role={selected}
                all={permissionItems}
                onDelete={selected && !selected.is_system ? () => setDeleting(selected) : undefined}
                onCreated={(id) => setPicked(id)}
                onNameChange={(ar, en) => setDraftNames({ ar, en })}
                onSaveState={setSaveState}
              />
            ) : null}
          </Box>
        </Box>
      )}
      <ConfirmDialog
        open={!!deleting}
        danger
        title={tr('حذف الدور', 'Delete role')}
        text={tr(`حذف الدور «${deleting ? roleNameAr(deleting) : ''}»؟`, `Delete role “${deleting ? roleNameEn(deleting) || roleNameAr(deleting) : ''}”?`)}
        confirmLabel={tr('حذف', 'Delete')}
        busy={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting.id)}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}

function RoleEditor({ role, all, onDelete, onCreated, onNameChange, onSaveState }: { role: Role | null; all: PermissionRow[]; onDelete?: () => void; onCreated: (id: string) => void; onNameChange: (nameAr: string, nameEn: string) => void; onSaveState: (s: { run: () => void; pending: boolean; disabled: boolean } | null) => void }) {
  const { me, can } = useAuth();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [nameAr, setNameAr] = useState(role ? roleNameAr(role) : '');
  const [nameEn, setNameEn] = useState(role ? roleNameEn(role) : '');
  const locked = role?.code === 'OWNER';
  const [on, setOn] = useState<string[]>(locked ? all.map((p) => p.code) : (role?.permissions ?? []));
  const isOwner = !!me?.user.is_owner;
  const ownRole = !!role && role.id === me?.user.role_id && !isOwner;
  const grantable = (code: string) => isOwner || can(code as Permission) || !!role?.permissions.includes(code);
  const groups = useMemo(() => groupsOf(all, on), [all, on]);
  const accId = useId();
  const english = getUiLanguage() === 'en';
  const save = useMutation({
    mutationFn: () => (role
      ? api(`/roles/${role.id}`, { method: 'PATCH', body: { ...(english ? { nameEn: nameEn.trim() } : { name: nameAr.trim() }), permissions: on } })
      : api<Role>('/roles', { method: 'POST', body: { ...(english ? { nameEn: nameEn.trim() } : { name: nameAr.trim() }), permissions: on } })),
    onSuccess: (created) => {
      toast(tr('تم حفظ الدور', 'Role saved'));
      void qc.invalidateQueries({ queryKey: ['roles'] });
      if (!role && created && typeof created === 'object' && created !== null && 'id' in created && typeof created.id === 'string') onCreated(created.id);
    },
    onError: toastError,
  });
  const toggle = (code: string) => setOn((s) => (s.includes(code) ? s.filter((x) => x !== code) : [...s, code]));
  const toggleGroup = (g: ReturnType<typeof groupsOf>[number]) => {
    const allOn = g.on === g.total;
    setOn((s) => (allOn ? s.filter((c) => !g.items.some((i) => i.code === c)) : [...new Set([...s, ...g.items.filter((i) => grantable(i.code)).map((i) => i.code)])]));
  };
  const disabled = ownRole || locked;
  const { mutate, isPending } = save;
  useEffect(() => {
    if (disabled) onSaveState(null);
    else onSaveState({ run: () => mutate(), pending: isPending, disabled: (english ? nameEn : nameAr).trim().length < 2 });
  }, [disabled, english, nameAr, nameEn, isPending, mutate, onSaveState]);
  useEffect(() => () => onSaveState(null), [onSaveState]);

  return (
    <Card>
      <Stack spacing={2} sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            label={tr('اسم الدور', 'Role name')}
            value={english ? nameEn : nameAr}
            onChange={(e) => {
              if (english) {
                setNameEn(e.target.value);
                onNameChange(nameAr, e.target.value);
              } else {
                setNameAr(e.target.value);
                onNameChange(e.target.value, nameEn);
              }
            }}
            disabled={disabled || role?.is_system}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { dir: english ? 'ltr' : 'rtl' } }}
          />
          {onDelete && (
            <Tooltip title={role && role.users_count > 0 ? tr('انقل الموظفين لدور آخر أولاً', 'Move employees to another role first') : tr('حذف', 'Delete')}>
              <span>
                <Button color="error" disabled={!!role && role.users_count > 0} startIcon={<DeleteIcon />} onClick={onDelete}>{tr('حذف', 'Delete')}</Button>
              </span>
            </Tooltip>
          )}
        </Stack>
        {save.isError && <Alert severity="error">{save.error instanceof Error ? save.error.message : tr('تعذر الحفظ', 'Unable to save')}</Alert>}
      </Stack>
      {groups.map((g, i) => {
        const allOn = g.on === g.total;
        return (
          <Accordion
            key={g.key}
            defaultExpanded={i === 0}
            disableGutters
            elevation={0}
            sx={{ bgcolor: 'transparent', '&:before': { display: 'none' }, borderTop: 1, borderColor: 'divider' }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${accId}-${g.key}-content`}
              id={`${accId}-${g.key}-header`}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  control={<Checkbox size="small" checked={allOn} indeterminate={g.on > 0 && !allOn} disabled={disabled} onChange={() => toggleGroup(g)} />}
                  label={<Typography fontWeight={700}>{PERMISSION_GROUP[g.key] ?? g.key}</Typography>}
                />
                <Typography variant="caption" color="text.secondary">{g.on}/{g.total}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List dense disablePadding>
                {g.items.map((p) => (
                  <ListItem key={p.code} disablePadding>
                    <ListItemButton disabled={disabled || (!on.includes(p.code) && !grantable(p.code))} onClick={() => !disabled && toggle(p.code)} sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={on.includes(p.code)}
                          disabled={disabled || (!on.includes(p.code) && !grantable(p.code))}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText primary={getUiLanguage() === 'en' ? PERMISSION_LABEL_EN[p.code] ?? p.description?.en ?? p.description?.ar ?? p.code : p.description?.ar ?? p.code} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Card>
  );
}
